/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { LoadingState, ErrorState, PaginationInfo, PaginatedResponse } from './types'
import {
  fetchNavigationData,
  fetchGlobalData,
  fetchHomepageData,
  fetchAboutData,
  fetchContactData,
  fetchPostsData,
  fetchPostsPaginated,
  fetchNewsListData,
  fetchDesignData,
  fetchTechnologyData,
  fetchSinglePost,
  fetchAdjacentPosts,
  fetchPageData,
  fetchSeoData
} from './api'
import { createComputedProperties } from './computed'
import { getImageUrl, processDynamicZone, makeSrcset, processImageWithFormats } from './config'

export const useStrapiStore = defineStore('strapi', () => {
  // State
  const globalData = ref<any>(null)
  const navigationData = ref<any>(null)
  const homepageData = ref<any>(null)
  const aboutData = ref<any>(null)
  const contactData = ref<any>(null)
  const postsData = ref<any>(null)
  const paginatedPostsData = ref<PaginatedResponse<any> | null>(null)
  const newsListData = ref<any>(null)
  const designData = ref<any>(null)
  const technologyData = ref<any>(null)
  const seoData = ref<any>(null)
  
  const loading = ref<LoadingState>({
    global: false,
    navigation: false,
    homepage: false,
    about: false,
    contact: false,
    posts: false,
    newslist: false,
    design: false,
    technology: false,
    seo: false
  })
  
  const errors = ref<ErrorState>({
    global: null,
    navigation: null,
    homepage: null,
    about: null,
    contact: null,
    posts: null,
    newslist: null,
    design: null,
    technology: null,
    seo: null
  })

  // Create computed properties
  const computedProps = createComputedProperties(
    globalData,
    navigationData,
    homepageData,
    aboutData,
    contactData,
    postsData,
    paginatedPostsData,
    newsListData,
    designData,
    technologyData,
    seoData
  )

  // Actions
  const fetchNavigation = async (): Promise<void> => {
    loading.value.navigation = true
    errors.value.navigation = null
    
    try {
      const data = await fetchNavigationData()
      navigationData.value = data
    } catch (error) {
      errors.value.navigation = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.navigation = false
    }
  }

  const fetchGlobal = async (): Promise<void> => {
    loading.value.global = true
    errors.value.global = null
    
    try {
      const data = await fetchGlobalData()
      globalData.value = data
    } catch (error) {
      errors.value.global = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.global = false
    }
  }

  const fetchHomepage = async (): Promise<void> => {
    loading.value.homepage = true
    errors.value.homepage = null
    
    try {
      const data = await fetchHomepageData()
      homepageData.value = data
    } catch (error) {
      errors.value.homepage = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.homepage = false
    }
  }

  const fetchAbout = async (): Promise<void> => {
    loading.value.about = true
    errors.value.about = null
    
    try {
      const data = await fetchAboutData()
      aboutData.value = data
    } catch (error) {
      errors.value.about = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.about = false
    }
  }

  const fetchContact = async (): Promise<void> => {
    loading.value.contact = true
    errors.value.contact = null
    
    try {
      const data = await fetchContactData()
      contactData.value = data
    } catch (error) {
      errors.value.contact = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.contact = false
    }
  }

  const fetchPosts = async (): Promise<void> => {
    loading.value.posts = true
    errors.value.posts = null
    
    try {
      const data = await fetchPostsData()
      postsData.value = data
    } catch (error) {
      errors.value.posts = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.posts = false
    }
  }

  const fetchPaginatedPosts = async (page: number = 1, pageSize: number = 12): Promise<void> => {
    loading.value.posts = true
    errors.value.posts = null
    
    try {
      const data = await fetchPostsPaginated(page, pageSize)
      paginatedPostsData.value = data
    } catch (error) {
      errors.value.posts = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.posts = false
    }
  }

  const fetchNewsList = async (): Promise<void> => {
    loading.value.newslist = true
    errors.value.newslist = null
    
    try {
      const data = await fetchNewsListData()
      newsListData.value = data
    } catch (error) {
      errors.value.newslist = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.newslist = false
    }
  }

  const fetchDesign = async (): Promise<void> => {
    loading.value.design = true
    errors.value.design = null
    
    try {
      const data = await fetchDesignData()
      designData.value = data
    } catch (error) {
      errors.value.design = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.design = false
    }
  }

  const fetchTechnology = async (): Promise<void> => {
    loading.value.technology = true
    errors.value.technology = null
    
    try {
      const data = await fetchTechnologyData()
      technologyData.value = data
    } catch (error) {
      errors.value.technology = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.technology = false
    }
  }

  const fetchSeo = async (): Promise<void> => {
    loading.value.seo = true
    errors.value.seo = null
    
    try {
      const data = await fetchSeoData()
      seoData.value = data
    } catch (error) {
      errors.value.seo = error instanceof Error ? error.message : 'Unknown error'
    } finally {
      loading.value.seo = false
    }
  }

  // Initialize all data
  const initialize = async (): Promise<void> => {
    await Promise.all([
      fetchNavigation(),
      fetchGlobal(),
      fetchHomepage(),
      fetchSeo()
    ])
  }

  // Initialize navigation separately
  const initializeNavigation = async (): Promise<void> => {
    if (!navigationData.value) {
      await fetchNavigation()
    }
  }

  // Initialize specific sections
  const initializeAbout = async (): Promise<void> => {
    if (!aboutData.value) {
      await fetchAbout()
    }
  }

  const initializeContact = async (): Promise<void> => {
    if (!contactData.value) {
      await fetchContact()
    }
  }

  const initializePosts = async (): Promise<void> => {
    if (!postsData.value) {
      await fetchPosts()
    }
  }

  const initializeNewslist = async (): Promise<void> => {
    if (!newsListData.value) {
      await fetchNewsList()
    }
  }

  const initializeDesign = async (): Promise<void> => {
    if (!designData.value) {
      await fetchDesign()
    }
  }

  const initializeTechnology = async (): Promise<void> => {
    if (!technologyData.value) {
      await fetchTechnology()
    }
  }

  return {
    // State
    globalData,
    navigationData,
    homepageData,
    aboutData,
    contactData,
    postsData,
    paginatedPostsData,
    newsListData,
    designData,
    technologyData,
    seoData,
    loading,
    errors,
    
    // Actions
    fetchNavigation,
    fetchGlobal,
    fetchHomepage,
    fetchAbout,
    fetchContact,
    fetchPosts,
    fetchPaginatedPosts,
    fetchNewsList,
    fetchDesign,
    fetchTechnology,
    fetchSeo,
    fetchSinglePost,
    fetchAdjacentPosts,
    fetchPage: fetchPageData,
    initialize,
    initializeNavigation,
    initializeAbout,
    initializeContact,
    initializePosts,
    initializeNewslist,
    initializeDesign,
    initializeTechnology,
    getImageUrl,
    processDynamicZone,
    makeSrcset,
    processImageWithFormats,
    
    // Computed properties
    ...computedProps
  }
})

// Re-export types for convenience
export type * from './types' 